import { ConfirmationStatus, NoticeStatusType, NoticeType } from '../enums';
import {
  ESnapshotExists,
  ENotice,
  EUser,
  EFirebaseContext,
  EOrganization,
  ETransaction,
  exists
} from '../types';
import { getOrThrow, getOrThrowTransaction } from '../utils/refs';
import { getCustomerForNotice } from './customer';
import { getNoticeType, removeUndefinedFields } from '../helpers';

type OrderNumberNoticeFields = Pick<
  Partial<ENotice>,
  'customId' | 'noticeType'
>;

function getShouldAutoGenerateOrderNumsForNotice(
  newspaper: ESnapshotExists<EOrganization>,
  parent: ESnapshotExists<EOrganization> | undefined,
  notice: OrderNumberNoticeFields
): boolean {
  const noticeAlreadyHasOrderNumber = !!notice.customId;
  if (noticeAlreadyHasOrderNumber) {
    return false;
  }

  const { shouldAutoGenOrderNums: childShouldAutoGenOrderNums } =
    newspaper.data();
  const { shouldAutoGenOrderNums: parentShouldAutoGenOrderNums } =
    parent?.data() || {};

  const shouldAutoGenOrderNums =
    childShouldAutoGenOrderNums ?? parentShouldAutoGenOrderNums ?? false;

  if (!shouldAutoGenOrderNums) {
    return false;
  }

  if (typeof shouldAutoGenOrderNums === 'boolean') {
    return shouldAutoGenOrderNums;
  }

  const isDisplay = notice.noticeType === NoticeType.display_ad.value;

  if (isDisplay) {
    return shouldAutoGenOrderNums.display;
  }

  return shouldAutoGenOrderNums.liners;
}

function getOrgGeneratingOrderNumbers(
  newspaper: ESnapshotExists<EOrganization>,
  parent: ESnapshotExists<EOrganization> | undefined,
  notice: OrderNumberNoticeFields
): ESnapshotExists<EOrganization> | undefined {
  if (!getShouldAutoGenerateOrderNumsForNotice(newspaper, parent, notice)) {
    return undefined;
  }

  return newspaper.data().shouldAutoGenOrderNums ? newspaper : parent;
}

export function getNoticeNumberAndCustomIdFromNewspaper(
  newspaper: ESnapshotExists<EOrganization>
): { currentNumber: number; customId: string } {
  const currentNumber = newspaper.data().numberOfOrders || 1;
  const numberOfDigits =
    newspaper?.data()?.orderNumberGeneration?.orderNumDigitCount || 4;
  const numOfZeros =
    numberOfDigits - `${currentNumber}`.length > 0
      ? numberOfDigits - `${currentNumber}`.length
      : 0;
  const prefix = newspaper?.data()?.orderNumberGeneration?.orderNumPrefix || '';
  const suffix = newspaper?.data()?.orderNumberGeneration?.orderNumSuffix || '';
  const customId = `${prefix}${'0'.repeat(
    numOfZeros
  )}${currentNumber}${suffix}`;

  return {
    currentNumber,
    customId
  };
}

async function generateCustomIdForNotice(
  transaction: ETransaction,
  notice: ESnapshotExists<ENotice>
) {
  const publisherOrganization = await getOrThrowTransaction(
    transaction,
    notice.data().newspaper
  );

  const parentOrganizationRef = publisherOrganization.data().parent;
  const parentOrganization = parentOrganizationRef
    ? await getOrThrowTransaction(transaction, parentOrganizationRef)
    : undefined;
  const orgToUpdate = getOrgGeneratingOrderNumbers(
    publisherOrganization,
    parentOrganization,
    notice.data()
  );

  if (!orgToUpdate) {
    return undefined;
  }

  const { customId, currentNumber } =
    getNoticeNumberAndCustomIdFromNewspaper(orgToUpdate);

  transaction.update(orgToUpdate.ref, {
    numberOfOrders: currentNumber + 1
  });

  return customId;
}

/** The order number this notice will be given at confirmation, before it exists. */
export async function getPendingAutoGeneratedOrderNumber(
  newspaper: ESnapshotExists<EOrganization>,
  notice: OrderNumberNoticeFields
): Promise<string | undefined> {
  const parentSnap = await newspaper.data().parent?.get();

  const orgGeneratingOrderNumbers = getOrgGeneratingOrderNumbers(
    newspaper,
    exists(parentSnap) ? parentSnap : undefined,
    notice
  );

  if (!orgGeneratingOrderNumbers) {
    return undefined;
  }

  return getNoticeNumberAndCustomIdFromNewspaper(orgGeneratingOrderNumbers)
    .customId;
}

async function updateNoticeWithConfirmation(
  ctx: EFirebaseContext,
  {
    notice,
    user
  }: {
    notice: ESnapshotExists<ENotice>;
    user: ESnapshotExists<EUser>;
  }
) {
  await ctx.runTransaction(async transaction => {
    const updates: Partial<ENotice> = {
      confirmationStatus: ConfirmationStatus.Confirmed,
      confirmedReceipt: true,
      confirmedBy: user.ref,
      confirmedReceiptTime: ctx.fieldValue().serverTimestamp() as any,
      customId: await generateCustomIdForNotice(transaction, notice)
    };

    transaction.update(notice.ref, removeUndefinedFields(updates));
  });
}
/** Instead of calling this directly, consider using the `confirm` method of `UserNoticeModel`. */
export async function confirmNotice(
  ctx: EFirebaseContext,
  {
    notice,
    user
  }: {
    notice: ESnapshotExists<ENotice>;
    user: ESnapshotExists<EUser>;
  }
) {
  await updateNoticeWithConfirmation(ctx, { notice, user });

  const customer = await getCustomerForNotice(ctx, notice);
  if (customer && !customer.data().verified) {
    await customer.ref.update({
      verified: true
    });
  }
}

/**
 * A notice should be auto-invoiced if:
 *  - autoInvoice is set on the newspaper, rate, or notice type AND
 *  - notice is not custom or cancelled
 *  - notice does not have an invoice already
 *  - notice was submitted with a proof
 */
export const shouldAutoInvoice = async (
  noticeSnap: ESnapshotExists<ENotice>
): Promise<boolean> => {
  const hasAnInvoiceAlready = !!noticeSnap.data().invoice;
  // Notices submitted without proof do not have pricing information yet
  const wasSubmittedWithoutProof = noticeSnap.data().postWithoutFormatting;
  const isCustomNoticetype =
    noticeSnap.data().noticeType === NoticeType.custom.value;
  const isCancelledNotice =
    noticeSnap.data().noticeStatus === NoticeStatusType.cancelled.value;
  if (
    hasAnInvoiceAlready ||
    wasSubmittedWithoutProof ||
    isCustomNoticetype ||
    isCancelledNotice
  ) {
    return false;
  }

  // TODO(COREDEV-1662): Use new parent boolean helper once merged!
  const newspaperSnap = await getOrThrow(noticeSnap.data().newspaper);
  const parentSnap = await newspaperSnap.data().parent?.get();
  const newspaperAutoInvoice =
    newspaperSnap.data().autoInvoice || parentSnap?.data()?.autoInvoice;

  if (newspaperAutoInvoice) {
    return true;
  }

  const rateSnap = await noticeSnap.data().rate?.get();
  if (rateSnap?.data()?.autoInvoice) {
    return true;
  }

  // If the notice is a display ad, we want to make sure we use the correct notice type settings by checking the previousNoticeType property
  const noticeTypeWithSettings = getNoticeType(noticeSnap, newspaperSnap, {
    skipDisplayType: true
  });
  if (noticeTypeWithSettings?.autoInvoice) {
    return true;
  }

  return false;
};

export const __private = {
  getShouldAutoGenerateOrderNumsForNotice
};
